import { getFunctions, httpsCallable } from "firebase/functions";
import * as axios from "axios";
import { UserData } from "../../User/UserData";
import { CompanyData } from "../../Company/CompanyData";
import { LockHolderData } from "../../Company/LockHolderData";
import { FirebaseEvents } from "../../../../utils/firebaseEvents";
import { LocksLoader } from "@pages/Property/Locks/LocksLoader";
import { Debug } from "../../../../utils/debug";
import { StringUtilities } from "../../../../utils/stringUtilities";
import { t } from "i18next";

import {
  UnlocKey,
  UnlocLock,
  UnlocLockHolder,
  UnlocRecurrence,
  UnlocRequestCreateKey,
  UnlocRequestCreateLockHolder,
  UnlocRole,
} from "./Unloc/UnlocTypes";
import { UnlocAPI } from "./Unloc/UnlocAPI";
import { ToastUtilities } from "../../../../libs/react-toastify/ToastUtilities";

export interface UnlocAuthTokenData {
  access_token: string;
  token_type: string;
  expires_in: number;
  scope: string[];
  lock_holder_id?: string;
}

export interface UnlocVendor {
  name: string;
  id: string;
  fields: { key: string; name: string; disableSave?: boolean }[];
}

export class UnlocHelper {
  static allVendors: UnlocVendor[] = [
    {
      id: "danalock",
      name: "Danalock",
      fields: [
        { key: "username", name: "Username" },
        { key: "password", name: t("main.password") },
      ],
    },
    {
      id: "masterlock",
      name: "Masterlock",
      fields: [
        { key: "lockName", name: t("main.name") },
        { key: "deviceIdentifier", name: "Device Identifier" },
        { key: "activationCode", name: "Activation code" },
      ],
    },
    {
      id: "integra",
      name: "Integra",
      fields: [
        { key: "username", name: "Username" },
        { key: "password", name: t("main.password") },
        { key: "loginServerUrl", name: "Login server url" },
        { key: "signalRServerUrl", name: "Signal R Server URL" },
      ],
    },
    {
      id: "rco",
      name: "RCO",
      fields: [
        { key: "username", name: "Username" },
        { key: "password", name: t("main.password") },
        { key: "siteId", name: "Site ID" },
        { key: "system", name: "System" },
        { key: "url", name: "URL" },
      ],
    },
    {
      id: "dormakaba-exivo",
      name: "Dormakaba Exivo",
      fields: [
        { key: "siteId", name: "Site ID" },
        { key: "siteName", name: "Site name" },
        { key: "apiKey", name: "API nøkkel", disableSave: true },
        { key: "apiSecret", name: "API secret", disableSave: true },
      ],
    },
    { id: "virtual", name: "Virtuell", fields: [] },
  ];

  static convertPhoneToE164(lockholder: LockHolderData | undefined, phone: string): string {
    try {
      if (typeof phone !== "string") {
        return "";
      }

      phone = StringUtilities.replaceAll(phone, " ", "");

      if (!phone.startsWith("+")) {
        const countryCode = lockholder?.countryIso.toLowerCase();
        if (countryCode === "no" && (!phone.startsWith("47") || phone.length === 8)) {
          phone = "47" + phone;
        } else if (countryCode === "dk" && (!phone.startsWith("45") || phone.length === 8)) {
          phone = "45" + phone;
        }
        return "+" + phone;
      }

      return phone;
    } catch (error) {
      console.error("Error on convertPhoneToE164");
      console.error(error);
      Debug.logUnloc("convertPhoneToE164", lockholder, error, { lockholder: lockholder?.id, phone: phone });
      return "";
    }
  }

  static generateUnscopedUnlocAuthToken(completion: (data?: UnlocAuthTokenData) => void) {
    const functions = getFunctions(undefined, "europe-west1");
    const generateUnscopedUnlocAuthTokenFunction = httpsCallable(functions, "generateUnscopedUnlocAuthToken");

    generateUnscopedUnlocAuthTokenFunction()
      .then((result) => {
        completion(result.data as UnlocAuthTokenData);
      })
      .catch((error: Error) => {
        console.log("Error getting unscoped auth token " + error.message);
        completion(undefined);
      });
  }

  static generateScopedUnlocAuthToken(lockHolder: LockHolderData, completion: (data?: UnlocAuthTokenData) => void) {
    const functions = getFunctions(undefined, "europe-west1");
    const generateScopedUnlocAuthTokenFunction = httpsCallable(functions, "generateScopedUnlocAuthToken");

    generateScopedUnlocAuthTokenFunction({
      companyID: lockHolder.company.id,
      lockHolderID: lockHolder.id,
    })
      .then((result) => {
        completion(result.data as UnlocAuthTokenData);
      })
      .catch((error: Error) => {
        console.log("Error generateScopedUnlocAuthToken " + error.message);
        completion(undefined);
      });
  }

  // LOCK HOLDERS
  static async createLockHolder(
    auth: UnlocAuthTokenData,
    company: CompanyData,
    name: string,
    countryIso: string,
    organizationId: string,
    organizationIdSuffix: string
  ): Promise<UnlocLockHolder> {
    const data: UnlocRequestCreateLockHolder = {
      name: name,
      countryIso: countryIso,
      organizationId: organizationId,
      organizationIdSuffix: organizationIdSuffix,
    };

    try {
      const result = await UnlocAPI.createLockholder(auth.access_token, data);
      await company.lockHolders.createData({
        unlocId: result.id,
        name: result.name,
        countryIso: result.countryIso,
        organizationId: result.organizationId,
        organizationIdSuffix: result.organizationIdSuffix,
      });

      return result;
    } catch (error) {
      throw new Error("Error");
    }
  }

  static async updateLockholderName(lockHolder: LockHolderData, name: string): Promise<void> {
    if (!lockHolder.scopedAuth.data) return Promise.reject();
    const result = await UnlocAPI.updateLockholderName(lockHolder, name);
    lockHolder.name = result.name ?? name;
    lockHolder.saveUpdateWithCompletionHandler({ name: name });
  }

  // VENDOR CONNECTIONS
  static updateVendorPassword(lockHolder: LockHolderData, vendorConnectionId: string, password: string, fbConnectionID: string): Promise<void> {
    if (!lockHolder.scopedAuth.data) return Promise.reject();

    return UnlocAPI.updateVendorPassword(lockHolder, vendorConnectionId, password)
      .then(() => {
        return new Promise<void>((resolve) => {
          lockHolder.fbVendorConnections.updateDocument(
            fbConnectionID,
            {
              password: password,
            },
            () => {
              const fbData = lockHolder.fbVendorConnections.getData(fbConnectionID);
              if (fbData) {
                fbData.password = password;
              }

              resolve();
            }
          );
        });
      })
      .catch((error) => {
        console.error("Error updating vendor password");
        Debug.logUnloc("updateVendorPassword", lockHolder, error);
        printError(error);
        throw error;
      });
  }

  static updateLockName(lockHolder: LockHolderData, lock: UnlocLock, name: string): Promise<UnlocLock> {
    if (!lockHolder.scopedAuth.data) return Promise.reject();

    return UnlocAPI.updateLockName(lockHolder, lock, name).then((response) => {
      lockHolder.locks.datas = lockHolder.locks.datas.filter((oldLock) => oldLock.id !== lock.id);
      const newLock = response;
      lockHolder.locks.datas.push(newLock);

      const fbLock = lockHolder.getFirebaseLockForUnlocLock(lock.id);
      if (fbLock) {
        fbLock.name = name;
        fbLock.saveUpdateWithCompletionHandler(
          {
            name: newLock.name,
          },
          (error) => {
            if (error) console.error(error);
            else console.log("Saved firebase name");
          }
        );
      } else {
        lockHolder.createLock(lock, undefined, (error) => {
          if (error) console.error(error);
        });
      }

      return newLock;
    });
  }

  static updateLockImage(lockHolder: LockHolderData, lock: UnlocLock, image: string): Promise<UnlocLock> {
    if (!lockHolder.scopedAuth.data) return Promise.reject();

    return UnlocAPI.updateLockImage(lockHolder, lock, image).then((response) => {
      lockHolder.locks.datas = lockHolder.locks.datas.filter((oldLock) => oldLock.id !== lock.id);
      const newLock = response;
      lockHolder.locks.datas.push(newLock);
      return newLock;
    });
  }

  //KEYS

  static async generateKeyForUser(
    lockHolder: LockHolderData | undefined,
    lock: UnlocLock,
    user: UserData,
    start?: Date | null,
    end?: Date | null,
    recurrence?: UnlocRecurrence
  ): Promise<UnlocKey> {
    if (!lockHolder) lockHolder = LocksLoader.getLockHolderForLock(lock);
    if (!lockHolder || !lockHolder.scopedAuth.data) return Promise.reject();

    const fbLock = lockHolder.getFirebaseLockForUnlocLock(lock.id);
    const phone = this.convertPhoneToE164(lockHolder, user.phone);

    const request: UnlocRequestCreateKey = {
      lockId: lock.id,
      userId: phone,
      start: start ? start.toISOString() : null,
      end: end ? end.toISOString() : null,
    };
    if (recurrence) request.recurrence = recurrence;

    const result = await UnlocAPI.createKey(lockHolder, request);
    lockHolder.keys.datas.push(result);
    FirebaseEvents.logEvent("unloc_key_shard", {});

    lockHolder.fbKeys
      .createData(
        {
          userId: user.id,
          userPhone: result.toUser.id,
          lockId: fbLock?.id ?? "",
          unlocLockId: lock.id,
          unlocKeyId: result.id,
        },
        result.id
      )
      .catch((e) => {
        console.error("Error creating firebase key");
        console.error(e);
      });

    return result;
  }

  static async generateKeyForPhone(
    lockHolder: LockHolderData | undefined,
    lock: UnlocLock,
    phone: string,
    start?: Date,
    end?: Date,
    recurrence?: UnlocRecurrence,
    name?: string
  ): Promise<UnlocKey> {
    if (!lockHolder) lockHolder = LocksLoader.getLockHolderForLock(lock);
    if (!lockHolder || !lockHolder.scopedAuth.data) return Promise.reject();

    phone = this.convertPhoneToE164(lockHolder, phone);
    const request: UnlocRequestCreateKey = {
      lockId: lock.id,
      userId: phone,
      start: start ? start.toISOString() : null,
      end: end ? end.toISOString() : null,
    };
    if (recurrence) request.recurrence = recurrence;

    try {
      const result = await UnlocAPI.createKey(lockHolder, request);
      ToastUtilities.presentSuccess(t("main.keySharedSuccessfully"));

      lockHolder.keys.datas.push(result);
      FirebaseEvents.logEvent("unloc_key_shard", {});
      const fbLock = lockHolder.getFirebaseLockForUnlocLock(lock.id);

      lockHolder.fbKeys
        .createData(
          {
            lockId: fbLock?.id ?? "",
            unlocLockId: lock.id,
            unlocKeyId: result.id,
            userPhone: phone,
          },
          result.id
        )
        .catch((e) => {
          console.error("Error creating firebase key");
          console.error(e);
        });
      if (name) {
        lockHolder.fbRoles.createData(
          {
            userName: name,
            userId: "",
          },
          result.toUser.id
        );
      }

      return result;
    } catch (error) {
      ToastUtilities.presentError(t("main.errorOccured"));
      throw error;
    }
  }

  static async revokeKey(lockHolder: LockHolderData | undefined, key: UnlocKey): Promise<UnlocKey> {
    if (!lockHolder) lockHolder = LocksLoader.getLockHolderForKey(key);
    if (!lockHolder || !lockHolder.scopedAuth.data) return Promise.reject();
    try {
      const result = await UnlocAPI.revokeKey(lockHolder, key);
      console.log(result);
      key = { ...result };

      if (lockHolder) {
        let newKeys = lockHolder.keys.datas;
        newKeys.push(key);
        newKeys = newKeys.filter((oldKey) => oldKey.id !== key.id);
        lockHolder.keys.datas = newKeys;

        lockHolder.fbKeys
          .deleteDocument(key.id)
          .then(() => console.log("Successfully deleted firebase key"))
          .catch((e) => console.error(e));
      }
      ToastUtilities.presentSuccess(t("main.keyRevokedSuccessfully"));
      return result;
    } catch (error) {
      ToastUtilities.presentError(t("main.errorOccured"));
      throw error;
    }
  }

  // ROLES
  static async updateRole(lockHolder: LockHolderData | undefined, lockId: string, userId: string, canShare: boolean): Promise<UnlocRole> {
    if (!lockHolder) lockHolder = LocksLoader.getLockHolderForLockId(lockId);
    if (!lockHolder || !lockHolder.scopedAuth.data) return Promise.reject();
    const result = await UnlocAPI.updateRole(lockHolder, lockId, userId, canShare);
    lockHolder.roles.datas = lockHolder.roles.datas.filter((role) => role.lockId !== lockId || role.userId !== userId);
    lockHolder.roles.datas.push(result);
    return result;
  }

  // CONFIG
  static getConfig(auth: UnlocAuthTokenData): axios.AxiosRequestConfig {
    return {
      headers: {
        Authorization: "Bearer " + auth.access_token,
        "Content-Type": "application/json",
      },
    };
  }
}

function printError(error: any) {
  console.error(error?.response?.data?.error + ". " + error?.response?.data?.errorDescription);
}
